// ============================================================
// TMR Portal Admin — create an operator's login in the Portal Interno (33)
// ============================================================
import { useState } from 'react';
import { Btn } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { SelectField, TextField } from '../../components/fields';
import { SearchBox } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { useGrantLegacyAccess, useLegacyProfileScreens, useLegacyProfiles } from '../../data/users/queries';
import type { GrantLegacyAccessResult, LegacyProfile, UserListItem } from '../../data/users/contract';
import {
  describeGrantFailure,
  filterScreens,
  isFutureExpiry,
  isGrantableProfile,
  profileOptionLabel,
  screenAbilities,
} from '../../data/users/legacyAccess';

/** Props for {@link CreateLegacyAccessModal}. */
interface CreateLegacyAccessModalProps {
  /** The account the login is created for: its name, e-mail and phone go to the legacy as they are. */
  user: UserListItem;
  onClose: () => void;
  /**
   * Opens the link picker on this account — the way out when a staff login already answers for the e-mail,
   * since creating a second one is exactly what must not happen.
   */
  onLinkInstead: () => void;
}

/**
 * Creates the account's login in the Portal Interno, with the screens of the chosen group, and links it.
 *
 * It replaces the SQL script that used to be run by hand (33 §1). The preview is the point of the screen: it
 * lists exactly what the login will open, so choosing a group is choosing what the person can do — not a
 * label that the screens are then typed in next to.
 */
export function CreateLegacyAccessModal({ user, onClose, onLinkInstead }: CreateLegacyAccessModalProps) {
  const [profileId, setProfileId] = useState<number | null>(null);
  const [expiresOn, setExpiresOn] = useState('');
  const [clinicPortalAccess, setClinicPortalAccess] = useState(false);
  const [screenSearch, setScreenSearch] = useState('');
  const [touched, setTouched] = useState(false);
  const [created, setCreated] = useState<GrantLegacyAccessResult | null>(null);

  const profiles = useLegacyProfiles(true);
  const screens = useLegacyProfileScreens(profileId);
  const grant = useGrantLegacyAccess();

  const chosen = profiles.data?.find((p) => p.id === profileId) ?? null;
  const expiryError = touched && !isFutureExpiry(expiresOn) ? 'Escolha uma data a partir de amanhã.' : null;
  const profileError = touched && !chosen ? 'Escolha o grupo de permissões.' : null;
  const failure = grant.error ? describeGrantFailure(grant.error) : null;

  const canSubmit = chosen !== null && isGrantableProfile(chosen) && isFutureExpiry(expiresOn) && !grant.isPending;

  const submit = () => {
    setTouched(true);
    if (!chosen || !isGrantableProfile(chosen) || !isFutureExpiry(expiresOn)) return;
    grant.mutate(
      { id: user.id, profileId: chosen.id, expiresOn, clinicPortalAccess },
      { onSuccess: (result) => setCreated(result) },
    );
  };

  if (created) return <CreatedView user={user} result={created} onClose={onClose} />;

  return (
    <ModalShell
      title="Criar acesso no Portal Interno"
      lead={`${user.name} · ${user.email}`}
      width={640}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 18 }}>
        {profiles.isPending ? (
          <Skeleton rows={2} />
        ) : profiles.isError ? (
          <ErrorState error={profiles.error} onRetry={() => void profiles.refetch()} />
        ) : profiles.data.length === 0 ? (
          <EmptyState message="Nenhum grupo de permissões no Portal Interno." />
        ) : (
          <SelectField
            label="Grupo de permissões"
            hint="O grupo decide quais telas do Portal Interno a pessoa abre e o que pode fazer em cada uma."
            required
            value={profileId === null ? '' : String(profileId)}
            onChange={(value) => {
              setProfileId(value ? Number(value) : null);
              setScreenSearch('');
            }}
            options={profiles.data.map((p) => ({ value: String(p.id), label: profileOptionLabel(p) }))}
            error={profileError}
          />
        )}

        {chosen && !isGrantableProfile(chosen) && (
          <Notice tone="warning">
            O grupo <strong>{chosen.name}</strong> não libera nenhuma tela. Ajuste-o em Permissão por Perfil, no
            Portal Interno, ou escolha outro grupo.
          </Notice>
        )}

        {chosen && isGrantableProfile(chosen) && (
          <ScreenPreview
            profile={chosen}
            screens={screens}
            search={screenSearch}
            onSearch={setScreenSearch}
          />
        )}

        <div style={{ maxWidth: 240 }}>
          <TextField
            label="Validade do acesso"
            hint="Depois dessa data, o Portal Interno pede uma senha nova no próximo acesso e a envia por e-mail."
            required
            type="date"
            value={expiresOn}
            onChange={setExpiresOn}
            error={expiryError}
          />
        </div>

        <label
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '12px 14px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={clinicPortalAccess}
            onChange={(event) => setClinicPortalAccess(event.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ fontSize: 13.5, lineHeight: 1.5 }}>
            <strong>Dar acesso também ao Portal da Clínica</strong>
            <br />
            <span style={{ color: 'var(--fg-muted)' }}>Todos os módulos e telas, como os demais membros da equipe.</span>
          </span>
        </label>

        {chosen && isGrantableProfile(chosen) && (
          <Notice tone="neutral">
            Vai ser criado o login <strong>{user.email}</strong> no grupo <strong>{chosen.name}</strong>, com{' '}
            {chosen.grantedScreenCount === 1 ? '1 tela' : `${chosen.grantedScreenCount} telas`}
            {clinicPortalAccess ? ' e o Portal da Clínica' : ''}. Nenhuma senha é definida: o primeiro acesso é
            por "Esqueci a senha".
          </Notice>
        )}

        {failure && (
          <Notice tone="danger">
            {failure.message}
            {failure.linkableLegacyUserId !== null && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={onLinkInstead}
                  style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 'inherit', color: 'inherit', textDecoration: 'underline' }}
                >
                  Vincular a este login
                </button>
              </>
            )}
          </Notice>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <Btn variant="secondary" onClick={onClose} disabled={grant.isPending}>
          Cancelar
        </Btn>
        <Btn
          variant="primary"
          icon={Icons.shield}
          onClick={submit}
          // Still clickable when the form is incomplete, so the click can say what is missing.
          disabled={grant.isPending || (touched && !canSubmit)}
        >
          {grant.isPending ? 'Criando…' : 'Criar acesso'}
        </Btn>
      </div>
    </ModalShell>
  );
}

/** Props for {@link ScreenPreview}. */
interface ScreenPreviewProps {
  profile: LegacyProfile;
  screens: ReturnType<typeof useLegacyProfileScreens>;
  search: string;
  onSearch: (value: string) => void;
}

/** The screens the chosen group opens — exactly what the login will receive. */
function ScreenPreview({ profile, screens, search, onSearch }: ScreenPreviewProps) {
  if (screens.isPending) return <Skeleton rows={4} />;
  if (screens.isError) return <ErrorState error={screens.error} onRetry={() => void screens.refetch()} />;

  const visible = filterScreens(screens.data, search);

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 12px', background: 'var(--cream-100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
          <strong style={{ color: 'var(--ink-900)' }}>{profile.name}</strong> abre{' '}
          {screens.data.length === 1 ? '1 tela' : `${screens.data.length} telas`}
        </span>
        <SearchBox value={search} onChange={onSearch} placeholder="Filtrar telas…" width={220} />
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {visible.length === 0 ? (
          <div style={{ padding: 14, fontSize: 12.5, color: 'var(--fg-subtle)' }}>Nenhuma tela com esse nome.</div>
        ) : (
          visible.map((s) => (
            <div
              key={s.screen}
              style={{ padding: '8px 12px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.screen}>
                {s.label}
              </span>
              <span style={{ color: 'var(--fg-subtle)', fontSize: 12, whiteSpace: 'nowrap' }}>{screenAbilities(s)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** Props for {@link CreatedView}. */
interface CreatedViewProps {
  user: UserListItem;
  result: GrantLegacyAccessResult;
  onClose: () => void;
}

/** What was created, and the one thing the person has to do next. */
function CreatedView({ user, result, onClose }: CreatedViewProps) {
  return (
    <ModalShell title="Acesso criado no Portal Interno" lead={`${user.name} · ${user.email}`} width={520} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
        <Notice tone="success">
          Login <span style={{ fontFamily: 'var(--font-mono)' }}>#{result.legacyUserId}</span> criado com{' '}
          {result.screenCount === 1 ? '1 tela' : `${result.screenCount} telas`}
          {result.clinicPortalModuleCount > 0 ? ' e o Portal da Clínica' : ''}, e já vinculado a esta pessoa.
        </Notice>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-muted)' }}>
          Para o primeiro acesso, peça a <strong>{user.name}</strong> que abra o Portal Interno, clique em{' '}
          <strong>Esqueci a senha</strong> e informe <strong>{user.email}</strong>. A senha chega por e-mail.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <Btn variant="primary" onClick={onClose}>
          Concluir
        </Btn>
      </div>
    </ModalShell>
  );
}
